/**
 * ReportEditor — analyst vetting surface for an authored monthly report.
 *
 * Two-column layout:
 *   - LEFT (2/3):  ReportPreview rendering exactly what the customer will see.
 *   - RIGHT (1/3): meta panel with status/actions, structured-data quick stats,
 *                  review checklist, custom content editor, audit trail.
 *
 * Lifecycle (drives which buttons are enabled):
 *   generating → draft → under_review → sent ("Published")
 *                                    ↘ rejected
 *   Approve publishes in one step. 'approved' only survives on reports
 *   approved before that change; they get a standalone Publish button.
 *
 * Backend endpoints (all proxied through analyst-api):
 *   PUT    /reports/{id}              — custom content / review notes
 *   POST   /reports/{id}/review       — checklist update + auto-promote draft→under_review
 *   POST   /reports/{id}/approve      — under_review|draft → sent (approve + publish)
 *   POST   /reports/{id}/reject       — reason required
 *   POST   /reports/{id}/regenerate   — delete + redo
 *   POST   /reports/{id}/send         — legacy approved → sent (publish)
 *   DELETE /reports/{id}              — refused for sent
 */

import { useEffect, useMemo, useState } from 'react';
import { format, parseISO } from 'date-fns';
import {
  ArrowLeft,
  CheckCircle,
  XCircle,
  RefreshCw,
  Send,
  Trash2,
  Loader2,
  AlertTriangle,
  Plus,
  Save,
  FileDown,
  Eye,
} from 'lucide-react';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Modal,
  Skeleton,
} from '../ui';
import { ReportPreview } from './ReportPreview';
import { SectionDrawer } from './SectionDrawer';
import { MarkdownField } from './MarkdownField';
import {
  getReport,
  updateReport,
  approveReport,
  rejectReport,
  reviewReport,
  regenerateReport,
  publishReport,
  deleteReport,
  exportReportPdf,
  getReportPdf,
  warmupPdfRenderer,
  getStatusLabel,
  getStatusColor,
  getReportTypeLabel,
} from '../../services/reports';
import type {
  ReportDetail,
  ReportReviewChecklistItem,
  ReportCustomContent,
  ContentPosition,
} from '../../data/types';

// The report→PDF export depends on func-axn-report-pdf, which is dev-only until
// the prod rollout (alexein/feature-adds/report_pdf_export.md). Gate the Export
// PDF button + renderer warmup on the deploy environment so the prod analyst
// func-axn-report-pdf is deployed in both environments (prod rollout
// 2026-08-05), so PDF export is enabled everywhere. The constant remains the
// single switch for every PDF affordance (export button, renderer warmup, and
// the requiresPdfArtifact approve/send gate below) should it ever need to be
// pulled again.
const PDF_EXPORT_ENABLED = true;

// Approve/Send require a rendered PDF artifact — but only where the analyst can
// actually produce one. Where PDF export is gated off, prod ops-api still
// returns the pre-PDF `pdfUrl` shape and never sets `pdfAvailable`, so gating on
// it unconditionally would leave both buttons permanently disabled with no way
// to satisfy them (the Generate PDF button that sets the flag is behind the same
// gate). Tying the requirement to PDF_EXPORT_ENABLED keeps the artifact gate
// real wherever the renderer exists and inert everywhere else, so a wholesale
// dev->main promotion can no longer brick report publication in prod.
const requiresPdfArtifact = (pdfAvailable?: boolean) =>
  PDF_EXPORT_ENABLED && !pdfAvailable;

interface ReportEditorProps {
  reportId: string;
  onBack: () => void;
  onReportUpdate?: () => void;
}

type ActionKey = 'approve' | 'reject' | 'regenerate' | 'publish' | 'delete' | 'save' | 'pdf' | null;

export function ReportEditor({ reportId, onBack, onReportUpdate }: ReportEditorProps) {
  const [report, setReport] = useState<ReportDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<ActionKey>(null);
  const [error, setError] = useState<string | null>(null);

  // Reject modal state
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  // Custom-content draft state (kept local until "Save edits" clicked)
  const [customDraft, setCustomDraft] = useState<ReportCustomContent[]>([]);
  const [customDirty, setCustomDirty] = useState(false);

  // Section drawer (per-section edit + Ask Claude). Null when closed.
  const [activeSection, setActiveSection] = useState<string | null>(null);

  // Service-delivery "other work" editor modal. The draft is seeded from the
  // authored sections on open and persisted via updateReport's
  // serviceDeliveryOtherWork patch (server-side edit of authoredSectionsJson).
  const [otherWorkOpen, setOtherWorkOpen] = useState(false);
  const [otherWorkDraft, setOtherWorkDraft] = useState('');

  // PDF export: rendered download link + progress messaging. The renderer
  // scales to zero, so we pre-warm it on open and keep the link around — a
  // popup-blocked window.open after a long render must not lose the PDF.
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [pdfNotice, setPdfNotice] = useState<string | null>(null);

  const loadReport = async () => {
    setLoading(true);
    setError(null);
    const result = await getReport(reportId);
    if (result.success) {
      setReport(result.data);
      setCustomDraft(result.data.customContent);
      setCustomDirty(false);
      if (!result.data.pdfAvailable) setPdfUrl(null);
    } else {
      setError(result.error ?? 'Failed to load report');
    }
    setLoading(false);
  };

  useEffect(() => {
    loadReport();
    // Boot the scale-to-zero PDF renderer in the background so an Export
    // click lands on a warm instance (cold renders can exceed the SWA's 45s
    // edge timeout). Fire-and-forget. Skipped where PDF export is gated off.
    if (PDF_EXPORT_ENABLED) void warmupPdfRenderer();
    setPdfUrl(null);
    setPdfNotice(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportId]);

  // -- derived flags ------------------------------------------------------
  const status = report?.status;
  const allChecked = useMemo(() => {
    if (!report) return false;
    return report.reviewChecklist.every((c) => c.isChecked);
  }, [report]);

  const canApprove = status === 'under_review' || status === 'draft';
  const canReject = status === 'draft' || status === 'under_review';
  const canRegenerate = status && status !== 'sent';
  // Only reports approved before approve-publishes landed sit at 'approved'.
  const canPublish = status === 'approved';
  // Published reports are also deletable, but the handler asks for an extra
  // confirm and sends ?force=true. Useful when a published issue was a test,
  // or when a newer corrected issue supersedes it.
  const canDelete = !!status;

  // -- handlers -----------------------------------------------------------
  const handleSaveCustom = async () => {
    if (!report) return;
    setActionLoading('save');
    const result = await updateReport(reportId, {
      customContent: customDraft.map((c) => ({
        sectionName: c.sectionName,
        customText: c.customText,
        position: c.position,
        customSectionTitle: c.customSectionTitle ?? undefined,
        displayOrder: c.displayOrder,
      })),
    });
    if (result.success) {
      await loadReport();
      onReportUpdate?.();
    } else {
      alert(`Save failed: ${result.error ?? 'unknown error'}`);
    }
    setActionLoading(null);
  };

  const handleApprove = async () => {
    if (!report) return;
    if (!allChecked) {
      alert('Verify all checklist items before approving.');
      return;
    }
    if (!confirm('Approve and publish this report? It appears on the customer portal immediately and can no longer be edited or regenerated.')) return;
    setActionLoading('approve');
    const result = await approveReport(reportId);
    if (result.success) {
      await loadReport();
      onReportUpdate?.();
    } else {
      alert(`Approve failed: ${result.error}`);
    }
    setActionLoading(null);
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      alert('Provide a rejection reason.');
      return;
    }
    setActionLoading('reject');
    const result = await rejectReport(reportId, { reason: rejectReason.trim() });
    if (result.success) {
      setShowRejectModal(false);
      setRejectReason('');
      await loadReport();
      onReportUpdate?.();
    } else {
      alert(`Reject failed: ${result.error}`);
    }
    setActionLoading(null);
  };

  const handleRegenerate = async () => {
    if (!confirm('Regenerate will delete the current draft and re-author it. Continue?')) return;
    setActionLoading('regenerate');
    const result = await regenerateReport(reportId);
    if (result.success) {
      // Regenerate creates a NEW reportId — bounce back to queue.
      onReportUpdate?.();
      onBack();
    } else {
      alert(`Regenerate failed: ${result.error}`);
    }
    setActionLoading(null);
  };

  const handlePublish = async () => {
    if (!confirm('Publish this report to the customer portal? This is irreversible.')) return;
    setActionLoading('publish');
    const result = await publishReport(reportId);
    if (result.success) {
      await loadReport();
      onReportUpdate?.();
    } else {
      alert(`Publish failed: ${result.error}`);
    }
    setActionLoading(null);
  };

  const handleExportPdf = async () => {
    setActionLoading('pdf');
    setPdfUrl(null);
    setPdfNotice('Rendering PDF — this can take up to a minute…');

    // One automatic retry: a cold renderer can blow past the SWA's 45s edge
    // timeout on the first attempt, but that attempt warms the instance, so
    // the retry comes back in seconds.
    let result = await exportReportPdf(reportId);
    if (!result.success) {
      setPdfNotice('Renderer was cold — retrying…');
      result = await exportReportPdf(reportId);
    }

    if (result.success && result.data?.pdfUrl) {
      setPdfUrl(result.data.pdfUrl);
      setReport((current) => current ? { ...current, pdfAvailable: true } : current);
      setPdfNotice(null);
      // Best effort: browsers may popup-block a window.open this long after
      // the click. The persistent Download button below is the reliable path.
      window.open(result.data.pdfUrl, '_blank', 'noopener');
    } else {
      setPdfNotice(null);
      alert(`PDF export failed: ${result.error ?? 'unknown error'}`);
    }
    setActionLoading(null);
  };

  const handleDownloadPdf = async () => {
    if (pdfUrl) {
      window.open(pdfUrl, '_blank', 'noopener');
      return;
    }
    setActionLoading('pdf');
    const result = await getReportPdf(reportId);
    if (result.success && result.data?.pdfUrl) {
      setPdfUrl(result.data.pdfUrl);
      window.open(result.data.pdfUrl, '_blank', 'noopener');
    } else {
      alert(`PDF download failed: ${result.error ?? 'unknown error'}`);
    }
    setActionLoading(null);
  };

  const handleDelete = async () => {
    const isSent = status === 'sent';
    const isApproved = status === 'approved';
    const message = isSent
      ? 'This report has been PUBLISHED to the customer. Deleting it removes it from their portal entirely. Cannot be undone. Continue?'
      : isApproved
        ? 'This report has been APPROVED but not yet published. Deleting will discard the approval. Cannot be undone. Continue?'
        : 'Delete this report draft? Cannot be undone.';
    if (!confirm(message)) return;
    setActionLoading('delete');
    const result = await deleteReport(reportId, { force: isSent });
    if (result.success) {
      onReportUpdate?.();
      onBack();
    } else {
      alert(`Delete failed: ${result.error}`);
    }
    setActionLoading(null);
  };

  const handleChecklistToggle = async (
    item: ReportReviewChecklistItem,
    checked: boolean,
  ) => {
    if (!report) return;
    const next = report.reviewChecklist.map((c) =>
      c.checkId === item.checkId ? { ...c, isChecked: checked } : c,
    );
    setReport({ ...report, reviewChecklist: next });
    // Fire-and-forget; on failure, refetch to get truth back.
    const result = await reviewReport(reportId, {
      checklist: [{ checkId: item.checkId, isChecked: checked }],
    });
    if (!result.success) {
      alert(`Checklist update failed: ${result.error}`);
      await loadReport();
    } else if (result.data?.allItemsChecked) {
      // Server auto-promoted to under_review; refresh to pick that up.
      await loadReport();
      onReportUpdate?.();
    }
  };

  const handleAddCustomBlock = () => {
    const next: ReportCustomContent = {
      contentId: `local-${Date.now()}`,
      sectionName: 'retro',
      customText: '',
      position: 'append',
      customSectionTitle: null,
      displayOrder: customDraft.length + 1,
      addedBy: 'you',
      addedAt: new Date().toISOString(),
    };
    setCustomDraft([...customDraft, next]);
    setCustomDirty(true);
  };

  const handleUpdateCustomBlock = (
    contentId: string,
    patch: Partial<ReportCustomContent>,
  ) => {
    setCustomDraft(
      customDraft.map((c) => (c.contentId === contentId ? { ...c, ...patch } : c)),
    );
    setCustomDirty(true);
  };

  const handleRemoveCustomBlock = (contentId: string) => {
    setCustomDraft(customDraft.filter((c) => c.contentId !== contentId));
    setCustomDirty(true);
  };

  // Drawer "save override" -> upsert a `position: 'replace'` row keyed by
  // sectionKey. Empty content removes the override (lets the AI prose show
  // through again). Mirrors the same dirty-tracking the sidebar form uses,
  // and immediately persists via updateReport so the preview re-renders
  // without making the analyst hunt for the Save edits button.
  const handleDrawerSaveOverride = async (sectionKey: string, content: string) => {
    const trimmed = content.trim();
    const next: ReportCustomContent[] = customDraft.filter(
      (c) => !(c.sectionName === sectionKey && c.position === 'replace'),
    );
    if (trimmed.length > 0) {
      next.push({
        contentId: `local-${Date.now()}`,
        sectionName: sectionKey,
        customText: trimmed,
        position: 'replace',
        customSectionTitle: null,
        displayOrder: next.length + 1,
        addedBy: 'you',
        addedAt: new Date().toISOString(),
      });
    }
    setCustomDraft(next);
    setCustomDirty(true);
    setActionLoading('save');
    const result = await updateReport(reportId, {
      customContent: next.map((c) => ({
        sectionName: c.sectionName,
        customText: c.customText,
        position: c.position,
        customSectionTitle: c.customSectionTitle ?? undefined,
        displayOrder: c.displayOrder,
      })),
    });
    if (result.success) {
      await loadReport();
      onReportUpdate?.();
    } else {
      alert(`Save failed: ${result.error ?? 'unknown error'}`);
    }
    setActionLoading(null);
  };

  const handleOpenOtherWork = () => {
    setOtherWorkDraft(report?.aiGeneratedContent?.serviceDelivery?.otherWork ?? '');
    setOtherWorkOpen(true);
  };

  // Persists immediately (like the drawer override) — '' clears the write-up.
  const handleSaveOtherWork = async () => {
    setActionLoading('save');
    const result = await updateReport(reportId, {
      serviceDeliveryOtherWork: otherWorkDraft,
    });
    if (result.success) {
      setOtherWorkOpen(false);
      await loadReport();
      onReportUpdate?.();
    } else {
      alert(`Save failed: ${result.error ?? 'unknown error'}`);
    }
    setActionLoading(null);
  };

  const drawerInitialOverride = activeSection
    ? customDraft.find(
        (c) => c.sectionName === activeSection && c.position === 'replace',
      )?.customText ?? ''
    : '';

  const sectionLabel = (key: string): string => ({
    spread: 'Feature spread',
    featureIntro: 'Charts intro',
    mitre: 'MITRE ATT&CK heatmap',
    retro: 'Incident retrospective',
    industry: 'Industry news',
    colophon: 'About this report',
  }[key] ?? key);

  // -- render ------------------------------------------------------------
  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-12" />
        <Skeleton className="h-96" />
      </div>
    );
  }

  if (error || !report) {
    return (
      <Card>
        <CardContent>
          <div className="py-12 text-center">
            <AlertTriangle className="w-12 h-12 mx-auto text-text-tertiary mb-4" />
            <h3 className="text-lg font-medium text-text-primary mb-2">
              {error ?? 'Report not found'}
            </h3>
            <Button onClick={onBack}>Go back</Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  const isReadOnly = status === 'approved' || status === 'sent';

  return (
    <div className="space-y-4">
      {/* Header */}
      <Card>
        <CardContent>
          <div className="flex items-start justify-between gap-4 p-2">
            <div className="flex items-start gap-3">
              <Button variant="ghost" size="sm" onClick={onBack}>
                <ArrowLeft className="w-4 h-4" />
              </Button>
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <h2 className="text-lg font-semibold text-text-primary">
                    {report.reportTitle}
                  </h2>
                  <Badge variant={getStatusColor(report.status)}>
                    {getStatusLabel(report.status)}
                  </Badge>
                </div>
                <div className="text-sm text-text-secondary">
                  {report.tenantDisplayName} · {getReportTypeLabel(report.reportType)} ·{' '}
                  {format(parseISO(report.periodStart), 'd MMM')} →{' '}
                  {format(parseISO(report.periodEnd), 'd MMM yyyy')}
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2 flex-wrap justify-end" data-report-actions>
              {/* Export PDF parked — window.print() can't produce a polished
                  magazine. Reviving as server-side Puppeteer render
                  (see go_live.md "PDF export Option C") in a follow-up.
                  Re-enable by uncommenting; Printer import retained. */}
              {canRegenerate && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleRegenerate}
                  disabled={!!actionLoading}
                >
                  {actionLoading === 'regenerate' ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <RefreshCw className="w-4 h-4 mr-1" />
                      Regenerate
                    </>
                  )}
                </Button>
              )}
              {canReject && (
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => setShowRejectModal(true)}
                  disabled={!!actionLoading}
                >
                  <XCircle className="w-4 h-4 mr-1" />
                  Reject
                </Button>
              )}
              {canApprove && (
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleApprove}
                  disabled={!!actionLoading || !allChecked || requiresPdfArtifact(report.pdfAvailable)}
                  title={!allChecked
                    ? 'Verify all checklist items first'
                    : requiresPdfArtifact(report.pdfAvailable)
                      ? 'Generate the final PDF before approval'
                      : 'Approve and publish to the customer portal'}
                >
                  {actionLoading === 'approve' ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4 mr-1" />
                      Approve &amp; publish
                    </>
                  )}
                </Button>
              )}
              {canPublish && (
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handlePublish}
                  disabled={!!actionLoading || requiresPdfArtifact(report.pdfAvailable)}
                  title={requiresPdfArtifact(report.pdfAvailable) ? 'Generate the final PDF before publishing' : undefined}
                >
                  {actionLoading === 'publish' ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-1" />
                      Publish
                    </>
                  )}
                </Button>
              )}
              {PDF_EXPORT_ENABLED && (
                <>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => window.open(`/reports/${reportId}/print`, '_blank', 'noopener')}
                    disabled={!!actionLoading}
                    title="Open the exact fixed-page PDF layout for review"
                  >
                    <Eye className="w-4 h-4 mr-1" />
                    PDF Preview
                  </Button>
                  {status !== 'sent' && (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={handleExportPdf}
                      disabled={!!actionLoading}
                      title="Render this report to PDF (takes ~10-30s; up to a minute when the renderer is cold)"
                    >
                      {actionLoading === 'pdf' ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin mr-1" />
                          {pdfNotice ?? 'Rendering…'}
                        </>
                      ) : (
                        <>
                          <FileDown className="w-4 h-4 mr-1" />
                          {report.pdfAvailable ? 'Regenerate PDF' : 'Generate PDF'}
                        </>
                      )}
                    </Button>
                  )}
                  {report.pdfAvailable && (
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={handleDownloadPdf}
                      title="Open the rendered PDF (link valid for 24h)"
                    >
                      <FileDown className="w-4 h-4 mr-1" />
                      Download PDF
                    </Button>
                  )}
                </>
              )}
              {canDelete && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleDelete}
                  disabled={!!actionLoading}
                  title={
                    status === 'sent'
                      ? 'Force-delete (removes from customer portal)'
                      : status === 'approved'
                        ? 'Delete approved report'
                        : 'Delete'
                  }
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Two-column body */}
      <div className="grid grid-cols-3 gap-4">
        <div className="col-span-2">
          <Card>
            <CardContent>
              <div className="overflow-x-auto">
                <ReportPreview
                  report={report}
                  onSectionEdit={isReadOnly ? undefined : (key) => setActiveSection(key)}
                  onEditOtherWork={isReadOnly ? undefined : handleOpenOtherWork}
                />
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="col-span-1 space-y-4">
          {/* Quick numbers from structuredData */}
          <Card>
            <CardHeader>
              <CardTitle>Numbers (from structured data)</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <Stat label="Alerts" value={report.structuredData.volume.total} />
                <Stat
                  label="Trend vs prior month"
                  value={
                    report.structuredData.volume.trendPct == null ||
                    report.structuredData.volume.previousTotal == null
                      ? 'First full month — no prior-period baseline'
                      : `${report.structuredData.volume.trendPct >= 0 ? '+' : ''}${report.structuredData.volume.trendPct}% (${report.structuredData.volume.previousTotal.toLocaleString('en-US')} → ${report.structuredData.volume.total.toLocaleString('en-US')})`
                  }
                />
                <Stat
                  label="High severity"
                  value={report.structuredData.volume.bySeverity.high}
                />
                <Stat
                  label="Resolved"
                  value={report.structuredData.resolution.resolvedCount}
                />
                <Stat
                  label="Top incidents"
                  value={report.structuredData.topIncidents.length}
                />
                <Stat
                  label="Industry updates"
                  value={report.structuredData.industryUpdates.length}
                />
              </div>
            </CardContent>
          </Card>

          {/* Review checklist */}
          {report.reviewChecklist.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Review checklist</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {report.reviewChecklist.map((item) => (
                    <label
                      key={item.checkId}
                      className="flex items-start gap-2 text-sm cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={item.isChecked}
                        disabled={isReadOnly}
                        onChange={(e) => handleChecklistToggle(item, e.target.checked)}
                        className="mt-0.5"
                      />
                      <div>
                        <div className="text-text-primary">{item.checkDescription}</div>
                        <div className="text-xs text-text-tertiary uppercase tracking-wider">
                          {item.sectionName}
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
                <div className="mt-3 text-xs text-text-tertiary">
                  {allChecked
                    ? 'All items verified — Approve & publish enabled.'
                    : `${report.reviewChecklist.filter((c) => c.isChecked).length}/${report.reviewChecklist.length} verified`}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Custom content editor */}
          <Card>
            <CardHeader>
              <CardTitle>Custom content</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {customDraft.length === 0 && (
                  <div className="text-sm text-text-tertiary">
                    No custom blocks. Use these to inject SOC notes into a section.
                  </div>
                )}
                {customDraft.map((block) => (
                  <div
                    key={block.contentId}
                    className="border border-border rounded p-3 space-y-2 bg-bg-secondary"
                  >
                    <div className="flex items-center gap-2 text-xs">
                      <select
                        value={block.sectionName}
                        disabled={isReadOnly}
                        onChange={(e) =>
                          handleUpdateCustomBlock(block.contentId, {
                            sectionName: e.target.value,
                          })
                        }
                        className="bg-bg-primary border border-border rounded px-2 py-1"
                      >
                        <option value="retro">retro</option>
                        <option value="spread">spread</option>
                        <option value="news">news</option>
                        <option value="colophon">about this report</option>
                      </select>
                      <select
                        value={block.position}
                        disabled={isReadOnly}
                        onChange={(e) =>
                          handleUpdateCustomBlock(block.contentId, {
                            position: e.target.value as ContentPosition,
                          })
                        }
                        className="bg-bg-primary border border-border rounded px-2 py-1"
                      >
                        <option value="prepend">prepend</option>
                        <option value="append">append</option>
                        <option value="replace">replace</option>
                      </select>
                      <input
                        type="text"
                        placeholder="Title (optional)"
                        value={block.customSectionTitle ?? ''}
                        disabled={isReadOnly}
                        onChange={(e) =>
                          handleUpdateCustomBlock(block.contentId, {
                            customSectionTitle: e.target.value || null,
                          })
                        }
                        className="flex-1 bg-bg-primary border border-border rounded px-2 py-1"
                      />
                      <button
                        onClick={() => handleRemoveCustomBlock(block.contentId)}
                        disabled={isReadOnly}
                        className="text-text-tertiary hover:text-red-500"
                        aria-label="Remove block"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    <textarea
                      value={block.customText}
                      disabled={isReadOnly}
                      onChange={(e) =>
                        handleUpdateCustomBlock(block.contentId, {
                          customText: e.target.value,
                        })
                      }
                      placeholder="Custom text… *italic* and **bold** are honoured."
                      rows={4}
                      className="w-full bg-bg-primary border border-border rounded p-2 text-sm font-mono"
                    />
                  </div>
                ))}
                {!isReadOnly && (
                  <div className="flex items-center gap-2">
                    <Button variant="secondary" size="sm" onClick={handleAddCustomBlock}>
                      <Plus className="w-3.5 h-3.5 mr-1" />
                      Add block
                    </Button>
                    {customDirty && (
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={handleSaveCustom}
                        disabled={actionLoading === 'save'}
                      >
                        {actionLoading === 'save' ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          <>
                            <Save className="w-3.5 h-3.5 mr-1" />
                            Save edits
                          </>
                        )}
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Audit trail */}
          <Card>
            <CardHeader>
              <CardTitle>Audit</CardTitle>
            </CardHeader>
            <CardContent>
              <dl className="text-xs space-y-1.5">
                <Row k="Generated" v={`${report.generatedBy ?? 'unknown'} · ${format(parseISO(report.generatedAt), 'd MMM HH:mm')}`} />
                {report.reviewedBy && (
                  <Row
                    k="Reviewed"
                    v={`${report.reviewedBy} · ${report.reviewedAt ? format(parseISO(report.reviewedAt), 'd MMM HH:mm') : ''}`}
                  />
                )}
                {report.approvedBy && report.approvedAt && (
                  <Row
                    k="Approved"
                    v={`${report.approvedBy} · ${format(parseISO(report.approvedAt), 'd MMM HH:mm')}`}
                  />
                )}
                {report.sentAt && (
                  <Row
                    k="Published"
                    v={format(parseISO(report.sentAt), 'd MMM HH:mm')}
                  />
                )}
                {report.reviewNotes && (
                  <div className="pt-2 mt-2 border-t border-border">
                    <div className="text-text-tertiary uppercase tracking-wider mb-1">
                      Review notes
                    </div>
                    <div className="text-text-primary whitespace-pre-wrap">
                      {report.reviewNotes}
                    </div>
                  </div>
                )}
              </dl>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Reject modal */}
      {showRejectModal && (
        <Modal
          isOpen={showRejectModal}
          onClose={() => setShowRejectModal(false)}
          title="Reject report"
        >
          <div className="space-y-3">
            <p className="text-sm text-text-secondary">
              Provide a reason. The report will move to <b>rejected</b> and the
              reason will be stored in review notes.
            </p>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              rows={4}
              placeholder="Why is this being rejected?"
              className="w-full bg-bg-primary border border-border rounded p-2 text-sm"
            />
            <div className="flex items-center justify-end gap-2">
              <Button variant="ghost" onClick={() => setShowRejectModal(false)}>
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={handleReject}
                disabled={!rejectReason.trim() || actionLoading === 'reject'}
              >
                {actionLoading === 'reject' ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  'Reject'
                )}
              </Button>
            </div>
          </div>
        </Modal>
      )}

      <SectionDrawer
        reportId={reportId}
        open={!!activeSection}
        sectionKey={activeSection}
        sectionLabel={activeSection ? sectionLabel(activeSection) : ''}
        initialOverride={drawerInitialOverride}
        readOnly={isReadOnly}
        onClose={() => setActiveSection(null)}
        onSaveOverride={handleDrawerSaveOverride}
      />

      {/* Service-delivery "other work" editor */}
      {otherWorkOpen && (
        <Modal
          isOpen={otherWorkOpen}
          onClose={() => setOtherWorkOpen(false)}
          title="Other work this month"
        >
          <div className="space-y-3">
            <p className="text-sm text-text-secondary">
              Free-form write-up of work not covered by the structured
              service-delivery categories. Rendered verbatim at the bottom of
              the report — the writing agent never edits it. Clear the text to
              remove the block.
            </p>
            <MarkdownField
              value={otherWorkDraft}
              onChange={setOtherWorkDraft}
              rows={8}
              placeholder={'e.g. "Added the FortiGate firewall into monitoring" or "Created a watchlist for finance-team sign-ins".'}
            />
            <div className="flex justify-end gap-2">
              <Button
                variant="secondary"
                onClick={() => setOtherWorkOpen(false)}
                disabled={actionLoading === 'save'}
              >
                Cancel
              </Button>
              <Button onClick={handleSaveOtherWork} disabled={actionLoading === 'save'}>
                {actionLoading === 'save' ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  'Save'
                )}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number | string }) {
  const display = typeof value === 'number' ? value.toLocaleString('en-US') : value;
  return (
    <div className="bg-bg-secondary p-2 rounded">
      <div className="text-lg font-semibold text-text-primary">{display}</div>
      <div className="text-xs text-text-tertiary">{label}</div>
    </div>
  );
}

function Row({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex items-baseline gap-2">
      <dt className="text-text-tertiary uppercase tracking-wider min-w-[70px]">{k}</dt>
      <dd className="text-text-primary">{v}</dd>
    </div>
  );
}
